if (!customElements.get('grid-banner-component')) {
  customElements.define(
    'grid-banner-component',
    class GridBannerComponent extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.selectors = {
          bannerInner: '.grid-banner__inner',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
          pagination: '.swiper-pagination',
        };

        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
          swiperSlide: 'swiper-slide',
        };

        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`[data-id="${this.sectionId}"]`);
        this.enableSwiperMobile = this.dataset.enableSwiperMobile === 'true';
        this.bannerInner = this.querySelector(this.selectors.bannerInner);

        this.sliderInstance = false;
        if (!this.enableSwiperMobile) return;
        if (!this.bannerInner) return;

        // Listen for mobile breakpoint changes
        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.init.bind(this);

        // Listen for resize to update slide widths
        this.handleResize = () => {
          if (this.sliderInstance && FoxTheme.config.mqlMobile) {
            const sliderItems = this.bannerInner.querySelectorAll(
              '.grid-banner__item.swiper-slide'
            );
            const slideWidth = window.innerWidth * 0.68;
            sliderItems.forEach((item) => {
              item.style.width = `${slideWidth}px`;
            });
            this.sliderInstance.slider.update();
          }
        };
        window.addEventListener('resize', this.handleResize);

        this.init();
      }

      init() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          this.destroySlider();
        }
      }

      initSlider() {
        if (typeof this.sliderInstance === 'object') return;

        // Get only items that should be in slider (not excluded)
        const sliderItems = this.bannerInner.querySelectorAll(
          '.grid-banner__item:not(.grid-banner__item--non-slider)'
        );

        if (sliderItems.length === 0) return;

        // Set slide widths for auto slidesPerView
        const slideWidth = window.innerWidth * 0.68; // 68vw
        sliderItems.forEach((item) => {
          item.style.width = `${slideWidth}px`;
          item.style.flexShrink = '0';
        });

        // Set gap to 1rem (15px)
        const spaceBetween = 15; // 1rem = 15px

        // Add swiper classes first
        this.classList.add(this.classes.swiper);
        this.bannerInner.classList.remove(this.classes.grid);
        this.bannerInner.classList.add(this.classes.swiperWrapper);

        // Add swiper-slide to items
        sliderItems.forEach((item) => {
          item.classList.add(this.classes.swiperSlide);
        });

        // Wait for DOM to update, then find elements and initialize
        setTimeout(() => {
          // Find navigation and pagination elements (they're inside the component)
          const nextEl = this.querySelector('.swiper-button-next');
          const prevEl = this.querySelector('.swiper-button-prev');
          const pagination = this.querySelector('.swiper-pagination');

          // Build slider options
          this.sliderOptions = {
            slidesPerView: 'auto',
            spaceBetween: spaceBetween,
            // Use slider mode instead of freeMode for smoother transitions
            freeMode: false,
            grabCursor: true,
            speed: 800, // Slower, smoother transition
            watchSlidesProgress: true,
            slideToClickedSlide: false,
            breakpoints: {
              768: {
                enabled: false,
              },
            },
            threshold: 5,
            mousewheel: {
              enabled: true,
              forceToAxis: true,
              sensitivity: 1,
            },
          };

          // Only add navigation if elements exist
          if (nextEl && prevEl) {
            this.sliderOptions.navigation = {
              nextEl: nextEl,
              prevEl: prevEl,
            };
          }

          // Only add pagination if element exists
          if (pagination) {
            this.sliderOptions.pagination = {
              el: pagination,
              type: 'progressbar',
            };
          }

          // Initialize Swiper
          this.sliderInstance = new window.FoxTheme.Carousel(
            this,
            this.sliderOptions,
            [FoxTheme.Swiper.Mousewheel]
          );
          this.sliderInstance.init();

          // Add manual click handlers for navigation
          // Swiper's built-in navigation doesn't work well with slidesPerView: 'auto'
          if (nextEl && prevEl && this.sliderInstance?.slider) {
            const swiper = this.sliderInstance.slider;

            // Store values for use in handlers
            this._sliderItems = sliderItems;
            this._spaceBetween = spaceBetween;
            this._bannerInner = this.bannerInner;

            const newNextHandler = (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!swiper || !this._sliderItems || !this._sliderItems.length) {
                return;
              }

              const sliderItems = this._sliderItems;
              const spaceBetween = this._spaceBetween;
              const slideWidth = sliderItems[0]?.offsetWidth || window.innerWidth * 0.68;

              const currentTranslate = swiper.getTranslate() || 0;
              const scrollAmount = slideWidth + spaceBetween;
              const containerWidth = this._bannerInner.offsetWidth;
              const totalContentWidth = sliderItems.length * slideWidth + (sliderItems.length - 1) * spaceBetween;
              const maxScroll = -(totalContentWidth - containerWidth);

              // Calculate new translate position
              const newTranslate = currentTranslate - scrollAmount;
              const finalTranslate = Math.max(maxScroll, newTranslate);

              // Animate smoothly using requestAnimationFrame
              const startTranslate = currentTranslate;
              const distance = finalTranslate - startTranslate;
              let startTime = Date.now();
              const duration = 800;

              const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(1, elapsed / duration);
                // Ease-in-out cubic
                const eased = progress < 0.5
                  ? 4 * progress * progress * progress
                  : 1 - Math.pow(-2 * progress + 2, 3) / 2;

                const currentPosition = startTranslate + distance * eased;
                swiper.setTranslate(currentPosition);

                // Update progress bar during animation
                if (this.updateProgressBar) this.updateProgressBar();

                if (progress < 1) {
                  requestAnimationFrame(animate);
                } else {
                  swiper.update();
                  if (this.updateProgressBar) this.updateProgressBar();
                }
              };

              requestAnimationFrame(animate);
            };

            const newPrevHandler = (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!swiper || !this._sliderItems || !this._sliderItems.length) {
                return;
              }

              const sliderItems = this._sliderItems;
              const spaceBetween = this._spaceBetween;
              const slideWidth = sliderItems[0]?.offsetWidth || window.innerWidth * 0.68;

              const currentTranslate = swiper.getTranslate() || 0;
              const scrollAmount = slideWidth + spaceBetween;
              const newTranslate = currentTranslate + scrollAmount;
              const finalTranslate = Math.min(0, newTranslate);

              // Animate smoothly using requestAnimationFrame
              const startTranslate = currentTranslate;
              const distance = finalTranslate - startTranslate;
              let startTime = Date.now();
              const duration = 800;

              const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(1, elapsed / duration);
                // Ease-in-out cubic
                const eased = progress < 0.5
                  ? 4 * progress * progress * progress
                  : 1 - Math.pow(-2 * progress + 2, 3) / 2;

                const currentPosition = startTranslate + distance * eased;
                swiper.setTranslate(currentPosition);

                // Update progress bar during animation
                if (this.updateProgressBar) this.updateProgressBar();

                if (progress < 1) {
                  requestAnimationFrame(animate);
                } else {
                  swiper.update();
                  if (this.updateProgressBar) this.updateProgressBar();
                }
              };

              requestAnimationFrame(animate);
            };

            // Store handlers on element for cleanup
            nextEl._customHandler = newNextHandler;
            prevEl._customHandler = newPrevHandler;

            // Remove any existing Swiper handlers first
            if (nextEl.onclick) nextEl.onclick = null;
            if (prevEl.onclick) prevEl.onclick = null;

            // Add listeners directly (don't clone - just add)
            nextEl.addEventListener('click', newNextHandler, { capture: true });
            prevEl.addEventListener('click', newPrevHandler, { capture: true });
          }

          // Update progress bar on scroll
          if (pagination && this.sliderInstance?.slider) {
            this.updateProgressBar = () => {
              const swiper = this.sliderInstance.slider;
              if (!swiper || !pagination) return;

              const progress = pagination.querySelector('.swiper-pagination-progressbar-fill');
              if (!progress) {
                // Progress bar might not exist yet, create it
                const progressBar = document.createElement('span');
                progressBar.className = 'swiper-pagination-progressbar-fill';
                pagination.appendChild(progressBar);
                return;
              }

              try {
                const currentTranslate = Math.abs(swiper.getTranslate() || 0);
                const containerWidth = this.bannerInner.offsetWidth;
                const slideWidth = sliderItems[0]?.offsetWidth || window.innerWidth * 0.68;
                const totalContentWidth = sliderItems.length * slideWidth + (sliderItems.length - 1) * spaceBetween;
                const maxScroll = Math.max(0, totalContentWidth - containerWidth);
                const progressPercent = maxScroll > 0 ? Math.min(100, (currentTranslate / maxScroll) * 100) : 0;

                progress.style.transform = `translate3d(0,0,0) scaleX(${progressPercent / 100})`;
                progress.style.transformOrigin = 'left top';
              } catch (e) {
                console.error('Progress bar update error:', e);
              }
            };

            // Initialize progress bar
            if (pagination && !pagination.querySelector('.swiper-pagination-progressbar-fill')) {
              const progressBar = document.createElement('span');
              progressBar.className = 'swiper-pagination-progressbar-fill';
              pagination.appendChild(progressBar);
            }

            // Listen to scroll events
            const updateOnScroll = () => {
              requestAnimationFrame(() => this.updateProgressBar());
            };

            this.sliderInstance.slider.on('progress', updateOnScroll);
            this.sliderInstance.slider.on('touchMove', updateOnScroll);
            this.sliderInstance.slider.on('slideChange', updateOnScroll);
            this.sliderInstance.slider.on('setTranslate', updateOnScroll);
            this.updateProgressBar();
          }

          this.handleAccessibility();
        }, 0);
      }

      handleAccessibility() {
        if (!this.sliderInstance) return;

        const focusableElements = FoxTheme.a11y?.getFocusableElements(this);

        if (focusableElements) {
          focusableElements.forEach((element) => {
            element.addEventListener('focusin', (event) => {
              if (event.relatedTarget !== null) {
                if (element.closest('.swiper-slide')) {
                  const slide = element.closest('.swiper-slide');
                  this.sliderInstance.slider.slideTo(
                    this.sliderInstance.slider.slides.indexOf(slide)
                  );
                }
              }
            });
          });
        }

        if (Shopify?.designMode) {
          document.addEventListener('shopify:block:select', (e) => {
            if (e.detail.sectionId !== this.sectionId) return;
            const index = Number(e.target.dataset.index);
            if (this.sliderInstance?.slider) {
              this.sliderInstance.slider.slideTo(index);
            }
          });
        }
      }

      destroySlider() {
        this.classList.remove(this.classes.swiper);
        this.bannerInner.classList.remove(this.classes.swiperWrapper);
        this.bannerInner.classList.add(this.classes.grid);

        // Remove swiper-slide from items and reset styles
        const sliderItems = this.bannerInner.querySelectorAll(`.${this.classes.swiperSlide}`);
        sliderItems.forEach((item) => {
          item.classList.remove(this.classes.swiperSlide);
          item.style.width = '';
          item.style.flexShrink = '';
        });

        if (typeof this.sliderInstance !== 'object') return;
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize);
        if (this.sliderInstance) {
          this.destroySlider();
        }
      }
    }
  );
}

